import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import ReusableTable from "../components/Table/ReusableTable";
import Button from "../components/Button/Button";
import { post, get } from "../utils/api";
import CustomDialog from "../components/dialog/CustomDialog";
import AddDistributorForm from "../components/OnboardingForm/DigitalFrom";
import { toast, ToastContainer } from "react-toastify";

const DigitalOnboardingListing = () => {
  const location = useLocation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusMap, setStatusMap] = useState({});
  const [detailStatusMap, setDetailStatusMap] = useState({});
  const [activeTab, setActiveTab] = useState("listing");

  // Dialog state
  const [isDialogVisible, setDialogVisible] = useState(false);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [esignInput, setEsignInput] = useState("");

  // Form state
  const [isFormVisible, setFormVisible] = useState(false);
  const [selectedDistributorFull, setSelectedDistributorFull] = useState(null);
  const [currentDistributor, setCurrentDistributor] = useState(null);

  // Ops team E-sign modal
  const [isOpsEsignModalVisible, setIsOpsEsignModalVisible] = useState(false);
  const [opsEsignName, setOpsEsignName] = useState("");
  const [opsEsignLoading, setOpsEsignLoading] = useState(false);
  const [selectedDistributorForOps, setSelectedDistributorForOps] = useState(null);

  const userDetails = JSON.parse(localStorage.getItem("user_details") || "{}");
  const roleId = userDetails?.role_id;
  const userEmail = userDetails?.email;
  const isDistributorUser = roleId === 2;
  const requestedTab = new URLSearchParams(location.search).get("tab");

  useEffect(() => {
    if (requestedTab === "form") {
      setActiveTab("form");
    }
  }, [requestedTab]);

  // Fetch status mappings
  useEffect(() => {
    const fetchStatusMap = async () => {
      try {
        const response = await get("/onboarding/onboarding-status");
        if (response.data?.Success && Array.isArray(response.data.Data)) {
          const map = {};
          response.data.Data.forEach((status) => {
            map[status.id] = status.status_desc;
          });
          setStatusMap(map);
        }
      } catch (err) {
        console.error("Error fetching status map:", err);
      }
    };

    const fetchDetailStatusMap = async () => {
      try {
        const resp = await get("/onboarding/onboarding-detail-status");
        if (resp.data?.Success && Array.isArray(resp.data.Data)) {
          const dmap = {};
          resp.data.Data.forEach((s) => {
            dmap[s.status_id] = s.status_desc;
          });
          setDetailStatusMap(dmap);
        }
      } catch (err) {
        console.error("Error fetching detail status:", err);
      }
    };

    fetchStatusMap();
    fetchDetailStatusMap();
  }, []);

  const getDistributorByEmail = useCallback(async () => {
    if (!userEmail) return null;

    try {
      const response = await get(
        `/onboarding/getDistributorByEmail?email=${encodeURIComponent(userEmail)}`
      );
      if (response.data?.Success && response.data.Data) {
        return response.data.Data;
      }
      return null;
    } catch (err) {
      console.error("Error fetching distributor by email:", err);
      return null;
    }
  }, [userEmail]);

  const formatDistributorData = useCallback(
    (rawData, currentUserDistributor) => {
      const formatted = rawData.map((dist) => {
        const isCurrentUserDistributor =
          currentUserDistributor &&
          (dist.distributor_email?.toLowerCase() === userEmail?.toLowerCase() ||
            dist.distributor_name === currentUserDistributor.distributor_name ||
            dist.arn_code === currentUserDistributor.arn_code);

        const allRecordsForEmail = rawData.filter(
          (d) => d.distributor_email === dist.distributor_email
        );
        const isAnyKycCompleted = allRecordsForEmail.some(
          (d) =>
            d.kyc_status?.toLowerCase() === "completed" ||
            d.kyc_status?.toLowerCase() === "approved"
        );

        return {
          distributorName: dist.distributor_name,
          arnCode: dist.arn_code,
          panNumber: dist.pan_number,
          bankAccountNumber: isCurrentUserDistributor
            ? currentUserDistributor.account_number
            : dist.account_number || "N/A",
          status: dist.kyc_status || "-",
          onboardingLink: dist.onboarding_link,
          id: dist.id,
          onboarding_form_status: dist.onboarding_form_status,
          onboarding_detail_status: dist.onboarding_detail_status,
          distributor_email: dist.distributor_email,
          fundName: dist.fund_name || "-",
          disableViewButton:
            roleId === 2 &&
            !isAnyKycCompleted &&
            (!dist.kyc_status || dist.kyc_status === "Pending"),
        };
      });

      if (roleId === 2 && currentUserDistributor) {
        return formatted.filter(
          (item) =>
            item.distributor_email?.toLowerCase() === userEmail?.toLowerCase() ||
            item.distributorName === currentUserDistributor.distributor_name ||
            item.arnCode === currentUserDistributor.arn_code
        );
      }

      if (roleId === 2 && !currentUserDistributor) {
        return formatted.filter(
          (item) => item.distributor_email?.toLowerCase() === userEmail?.toLowerCase()
        );
      }

      if (roleId === 3) {
        return formatted.filter((item) => item.onboarding_detail_status === 3);
      }

      return formatted;
    },
    [roleId, userEmail]
  );

  const fetchOnboardingForm = useCallback(async (id) => {
    try {
      setLoading(true);
      const resp = await get(`/onboarding/getOnboardingForm?id=${id}`);
      if (resp.data?.Success) {
        setSelectedDistributorFull(resp.data.Data);
        setFormVisible(true);
        setActiveTab("form");
      } else {
        toast.error(resp.data?.Message || "Failed to fetch form");
      }
    } catch (err) {
      console.error("Error fetching form:", err);
      toast.error("Error fetching form");
    } finally {
      setLoading(false);
    }
  }, []);

  const openFormTab = useCallback(async () => {
    if (isDistributorUser && currentDistributor?.id) {
      await fetchOnboardingForm(currentDistributor.id);
      return;
    }

    setSelectedDistributorFull(null);
    setFormVisible(true);
    setActiveTab("form");
  }, [currentDistributor, fetchOnboardingForm, isDistributorUser]);

  const fetchDistributors = useCallback(async () => {
    try {
      setLoading(true);

      let currentUserDistributor = null;
      if (roleId === 2) {
        currentUserDistributor = await getDistributorByEmail();
        setCurrentDistributor(currentUserDistributor);
      }

      let url = "/onboarding/listOnboardingForms";
      if (roleId === 2 && userEmail) {
        url += `?email=${encodeURIComponent(userEmail)}`;
      }

      const response = await get(url);

      if (response.data?.Success && Array.isArray(response.data.Data)) {
        const formattedData = formatDistributorData(
          response.data.Data,
          currentUserDistributor
        );
        setData(formattedData);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error("Error fetching distributors:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [formatDistributorData, getDistributorByEmail, roleId, userEmail]);

  useEffect(() => {
    if (Object.keys(statusMap).length && Object.keys(detailStatusMap).length) {
      fetchDistributors();
    }
  }, [statusMap, detailStatusMap, fetchDistributors]);

  useEffect(() => {
    if (!isDistributorUser || activeTab !== "form" || isFormVisible || loading) {
      return;
    }

    if (currentDistributor?.id) {
      fetchOnboardingForm(currentDistributor.id);
      return;
    }

    setSelectedDistributorFull(null);
    setFormVisible(true);
  }, [
    activeTab,
    currentDistributor,
    fetchOnboardingForm,
    isDistributorUser,
    isFormVisible,
    loading,
  ]);

  const handleBackToListing = useCallback(async () => {
    setFormVisible(false);
    setSelectedDistributorFull(null);
    setActiveTab("listing");
    await fetchDistributors();
  }, [fetchDistributors]);

  const handleOpsEsignSubmit = async () => {
    if (!selectedDistributorForOps?.id) {
      alert("Missing distributor id");
      return;
    }

    if (!opsEsignName.trim()) {
      alert("Please enter the name for E-sign");
      return;
    }

    const contractId = localStorage.getItem("signzy_contractId");
    if (!contractId) {
      alert("Contract ID not found. Distributor must complete E-sign first.");
      return;
    }

    try {
      setOpsEsignLoading(true);

      const payload = {
        name_2: opsEsignName.trim(),
        contractId,
        distributor_id: selectedDistributorForOps.id,
      };

      const signzyResp = await post("/eSign/initiate-signzy-contract-amc", payload);

      if (!signzyResp.data?.Success) {
        alert(signzyResp.data?.Message || "Failed to initiate AMC Signzy contract");
        setOpsEsignLoading(false);
        return;
      }

      const responseData = signzyResp.data.Data;
      const amcContractId = responseData?.contractId;

      if (amcContractId) {
        localStorage.setItem("ops_contractId", amcContractId);
      }

      const signerUrl = responseData?.signerdetail?.[0]?.workflowUrl;
      if (!signerUrl) {
        alert("Signer URL missing from Signzy AMC response!");
        setOpsEsignLoading(false);
        return;
      }

      window.open(signerUrl, "_blank");

      const approveResp = await post("/onboarding/amc/approve", {
        id: selectedDistributorForOps.id,
        esign_name: opsEsignName.trim(),
      });

      if (approveResp.data?.Success) {
        alert(approveResp.data.Message || "Approved by AMC with E-sign");
      } else {
        alert(approveResp.data?.Message || "AMC approval failed");
      }

      setIsOpsEsignModalVisible(false);
      setOpsEsignName("");
      setSelectedDistributorForOps(null);
      await fetchDistributors();
    } catch (err) {
      console.error("AMC E-sign error:", err);
      alert("AMC E-sign failed. Check console.");
    } finally {
      setOpsEsignLoading(false);
    }
  };

  const handleEsignSubmit = async () => {
    if (!selectedDistributor) return;

    if (!esignInput.trim()) {
      alert("Please enter the name for 'In the presence of'");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name_1: esignInput.trim(),
        distributor_id: selectedDistributor.id,
        witness_name: esignInput.trim(),
      };

      const signzyResp = await post("/eSign/initiate-signzy-contract", payload);

      if (!signzyResp.data?.Success) {
        alert(signzyResp.data?.Message || "Failed to initiate Signzy contract");
        setLoading(false);
        return;
      }

      const responseData = signzyResp.data.Data;
      const contractId = responseData?.contractId;
      const signerUrl = responseData?.signerdetail?.[0]?.workflowUrl;

      if (!contractId) {
        alert("Contract ID missing from Signzy response!");
        setLoading(false);
        return;
      }

      if (!signerUrl) {
        alert("Signer workflow URL missing!");
        setLoading(false);
        return;
      }

      localStorage.setItem("signzy_contractId", contractId);
      window.open(signerUrl, "_blank");

      const esignResp = await post("/onboarding/esign/complete", {
        distributor_id: selectedDistributor.id,
      });

      if (esignResp.data?.Success) {
        toast.success(esignResp.data.Message);
      } else {
        toast.error(esignResp.data?.Message || "Failed to complete e-sign");
      }

      setDialogVisible(false);
      await fetchDistributors();
    } catch (err) {
      console.error("E-sign error:", err);
      alert("E-sign failed. Check console.");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { Header: "Distributor Name", accessor: "distributorName", size: 200 },
      { Header: "APRN Code", accessor: "arnCode", size: 150 },
      { Header: "PAN Number", accessor: "panNumber", size: 150 },
      {
        Header: "Status",
        accessor: "status",
        size: 180,
        Cell: ({ value }) => {
          let colorClass = "bg-yellow-100 text-yellow-800";
          const normalized = (value || "").toLowerCase();
          const isPending = normalized.trim() === "pending" || normalized.trim() === "" || normalized.trim() === "-";

          if (normalized.includes("approve")) {
            colorClass = "bg-green-100 text-green-800";
          } else if (normalized.includes("reject")) {
            colorClass = "bg-red-100 text-red-800";
          } else if (normalized.includes("draft")) {
            colorClass = "bg-slate-100 text-slate-700";
          }

          if (isPending) {
            return (
              <span
                className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800"
                style={{ animation: "blink-pending 1.2s step-start infinite" }}
              >
                Pending
                <style>{`@keyframes blink-pending { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
              </span>
            );
          }

          const display = value || "-";

          return (
            <span className={`px-2 py-1 rounded text-xs font-medium ${colorClass}`}>
              {display}
            </span>
          );
        },
      },
      {
        Header: "Action",
        accessor: "action",
        size: 350,
        Cell: ({ row }) => {
          const distributor = row.original;

          return (
            <div className="flex items-center gap-4">
              <button
                onClick={() => fetchOnboardingForm(distributor.id)}
                disabled={distributor.disableViewButton}
                className={`text-blue-500 hover:text-blue-700 text-[11px] font-medium transition-colors ${
                  distributor.disableViewButton ? "opacity-30 cursor-not-allowed" : ""
                }`}
                title="View"
              >
                View
              </button>

              {roleId === 3 && (
                <button
                  onClick={() => {
                    setSelectedDistributorForOps(distributor);
                    setOpsEsignName("");
                    setIsOpsEsignModalVisible(true);
                  }}
                  disabled={distributor.onboarding_detail_status !== 3}
                  className={`text-blue-600 hover:text-blue-800 text-[11px] font-medium transition-colors ${
                    distributor.onboarding_detail_status !== 3
                      ? "opacity-30 cursor-not-allowed"
                      : ""
                  }`}
                  title="Approve & E-sign"
                >
                  Approve & E-sign
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [fetchOnboardingForm, roleId]
  );

  return (
    <div className="w-full max-w-screen-xl mx-auto p-4 space-y-6">
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar theme="colored" />

      <div className="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">KYC</h1>
            <p className="text-sm text-gray-500 font-medium">
              Listing and form are now merged into one workspace.
            </p>
          </div>

          {isDistributorUser && (
            <Button
              variant="default"
              onClick={openFormTab}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl shadow-lg shadow-indigo-100"
            >
              {currentDistributor?.id ? "Continue KYC" : "Start KYC"}
            </Button>
          )}
        </div>

        <div className="flex flex-wrap gap-3 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={() => setActiveTab("listing")}
            className={`rounded-xl px-4 py-2 text-sm font-bold transition-all ${
              activeTab === "listing"
                ? "bg-indigo-600 text-white shadow-md shadow-indigo-100"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            KYC Listing
          </button>
          <button
            type="button"
            onClick={openFormTab}
            className={`rounded-xl px-4 py-2 text-sm font-bold transition-all ${
              activeTab === "form"
                ? "bg-indigo-600 text-white shadow-md shadow-indigo-100"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            KYC Form
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden min-h-[160px]">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
            <div className="w-8 h-8 border-2 border-gray-100 border-t-indigo-500 rounded-full animate-spin" />
            <span className="text-xs font-bold uppercase tracking-widest">Syncing Registry...</span>
          </div>
        ) : activeTab === "listing" ? (
          <div className="p-1">
            {data.length === 0 ? (
              <div className="text-center py-20 text-gray-400 font-medium text-sm">
                No records found.
              </div>
            ) : (
              <div className="overflow-x-auto custom-registry-table">
                <style>{`
                  .custom-registry-table th {
                    color: #4f46e5;
                    font-size: 11px;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    padding: 16px 12px;
                    border-bottom: 1px solid #f3f4f6;
                  }
                  .custom-registry-table td {
                    padding: 16px 12px;
                    vertical-align: middle;
                  }
                `}</style>
                <ReusableTable columns={columns} data={data} />
              </div>
            )}
          </div>
        ) : isFormVisible ? (
          <AddDistributorForm
            userId={userDetails?.id || userDetails?.user_Id}
            distributorData={selectedDistributorFull}
            onBack={handleBackToListing}
            readOnly={
              selectedDistributorFull
                ? !(
                    roleId === 2 &&
                    [1, 2].includes(selectedDistributorFull.onboarding_form_status)
                  )
                : false
            }
            roleId={roleId}
          />
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-center text-gray-500">
            <p className="text-lg font-bold text-gray-800">Open a KYC record to continue</p>
            <p className="text-sm font-medium max-w-lg">
              Select a distributor from the listing to review the KYC form, or use the
              form tab to start or continue the distributor KYC flow.
            </p>
          </div>
        )}
      </div>

      <CustomDialog
        visible={isDialogVisible}
        onHide={() => setDialogVisible(false)}
        header={`E-sign for ${selectedDistributor?.distributorName}`}
      >
        <div className="flex flex-col gap-4">
          <label className="font-medium">In the presence of:</label>

          <input
            type="text"
            className="border rounded px-3 py-2"
            value={esignInput}
            onChange={(e) => {
              if (/^[A-Za-z\s]*$/.test(e.target.value)) {
                setEsignInput(e.target.value);
              }
            }}
            placeholder="Enter name"
          />

          <Button variant="default" onClick={handleEsignSubmit}>
            Submit
          </Button>
        </div>
      </CustomDialog>

      <CustomDialog
        visible={isOpsEsignModalVisible}
        onHide={() => setIsOpsEsignModalVisible(false)}
        header={`Approve & E-sign - ${selectedDistributorForOps?.distributorName}`}
        width="50vw"
      >
        <div className="space-y-4">
          <input
            type="text"
            value={opsEsignName}
            onChange={(e) => setOpsEsignName(e.target.value)}
            placeholder="In presence of"
            className="w-full px-3 py-2 border rounded"
          />

          <div className="flex justify-end gap-3">
            <Button
              variant="cancel"
              onClick={() => setIsOpsEsignModalVisible(false)}
              disabled={opsEsignLoading}
            >
              Cancel
            </Button>

            <Button
              variant="default"
              onClick={handleOpsEsignSubmit}
              disabled={opsEsignLoading || !opsEsignName.trim()}
            >
              {opsEsignLoading ? "Submitting..." : "Submit E-sign"}
            </Button>
          </div>
        </div>
      </CustomDialog>
    </div>
  );
};

export default DigitalOnboardingListing;
